import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import PageSEO from '../components/PageSEO';
import './ConsultingSolutionsPage.css';

const ConsultingSolutionsPage = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const pillars = [
    {
      id: 'leadership',
      label: 'Pillar 1',
      title: 'Leadership Development & Coaching',
      description: 'Strengthening leadership capability, alignment, and performance at every level of the organization.',
      offerings: [
        {
          num: '01',
          title: 'Executive & Senior Leader Coaching',
          desc: '1:1 coaching to strengthen capability, navigate high-stakes decision-making, and enhance leadership effectiveness.',
          format: 'Monthly sessions, typically in 6-month engagements',
        },
        {
          num: '02',
          title: 'Senior Team Alignment',
          desc: 'Facilitated session(s) around strategy, roles, accountability, priorities, integration of AI in the workforce and/or culture — ensuring growth does not erode team cohesion and organizational performance.',
          format: 'Half-day to 2-day intensive + structured follow-up',
        },
        {
          num: '03',
          title: 'Leadership Development Cohorts',
          desc: 'Cohort-based development experiences combining structured peer mentoring with leadership development, leveraging a deep network of subject matter experts.',
          format: '6-month cohort program for 6-24 leaders',
        },
        {
          num: '04',
          title: 'Leading the Hybrid Workforce: Managing People and AI-Powered Teammates',
          desc: 'As artificial intelligence becomes embedded in everyday work, leaders are increasingly responsible for hybrid workforces that combine people, AI-powered tools, digital agents, and automated workflows. This interactive workshop equips leadership teams with the mindset, skills, and frameworks needed to lead effectively in this new environment.',
          format: 'Interactive workshop, half-day to full-day',
        },
      ],
    },
    {
      id: 'talent',
      label: 'Pillar 2',
      title: 'Talent Infrastructure',
      description: 'Building the people strategy, staffing systems, and succession capacity that growing organizations need.',
      offerings: [
        {
          num: '05',
          title: 'Staffing Strategy & Talent Planning',
          desc: 'Staffing models, hiring frameworks, and talent planning grounded in cultural fit and institutional growth.',
          format: '8-12 week project engagement',
        },
        {
          num: '06',
          title: 'Fractional HR / People Leadership',
          desc: 'Executive-grade people strategy without full-time overhead. A flexible retainer scaled to your organization\'s size and stage.',
          format: 'Monthly retainer, 6 month minimum',
        },
        {
          num: '07',
          title: 'Succession Planning',
          desc: 'Develop a strong bench of potential successors for key roles, ensuring sustained impact and continuity across leadership transitions.',
          format: 'Custom-scoped to roles and candidates assessed',
        },
      ],
    },
    {
      id: 'governance',
      label: 'Pillar 3',
      title: 'Governance Support',
      description: 'Strengthening board capability, oversight, and the critical alignment between governance and executive leadership.',
      offerings: [
        {
          num: '07',
          title: 'Board Coaching',
          desc: 'Strengthens board capability, oversight, and decision-making through sustained coaching engagements that build governance effectiveness over time.',
          format: '6-month to 1-year engagement',
        },
        {
          num: '09',
          title: 'Board Workshops (or Retreats)',
          desc: 'Retreats and advisory support to align governance and executive leadership around mission, oversight rhythm, growth strategy, and strategic decisions.',
          format: '1-2 day retreat + structured follow-up',
        },
      ],
    },
  ];

  const engagementModels = [
    { title: 'Strategic Advisory', desc: 'Ongoing counsel on talent strategy, organizational design, and people infrastructure.' },
    { title: 'Coaching Engagements', desc: 'Targeted 1:1 or team coaching to develop leadership effectiveness and alignment.' },
    { title: 'Fractional Leadership', desc: 'Senior people leadership embedded in your organization on a part-time basis.' },
    { title: 'Workshops & Retreats', desc: 'Focused sessions for leadership teams, boards, or cross-functional groups.' },
    { title: 'Multi-Month Partnerships', desc: 'Extended engagements combining advisory, coaching, leadership development and implementation support.' },
  ];

  return (
    <div className="cs-page" data-testid="consulting-solutions-page">
      <PageSEO
        title="Consulting Solutions"
        path="/consulting-solutions"
        description="Board workshops, talent strategy development, HR audit and compliance review, and leadership coaching. Strategic consulting for small businesses and civic organizations."
      />
      <Header />

      {/* Hero */}
      <section className="cs-hero" data-testid="cs-hero">
        <div className="cs-container">
          <div className="cs-hero-badge">CONSULTING SOLUTIONS</div>
          <h1 className="cs-hero-title">Strategic Talent, Leadership,<br/>and Organizational Consulting</h1>
          <div className="cs-hero-line"></div>
          <p className="cs-hero-subtitle">
            Practical, senior-level support that strengthens leadership, talent infrastructure, organizational alignment, governance, and people strategy for growing organizations.
          </p>
        </div>
      </section>

      {/* Overview */}
      <section className="cs-overview" data-testid="cs-overview">
        <div className="cs-container">
          <div className="cs-overview-grid">
            <div className="cs-overview-content">
              <h2>Senior-Level Expertise. Practical Impact.</h2>
              <p>
                R6 helps organizations solve leadership, talent, governance, and people infrastructure challenges with practical, senior-level support. Every engagement is led by senior partners who bring deep experience across diverse sectors and organizational types.
              </p>
              <p>
                Whether you need an executive coach, a fractional HR leader, a board advisor, or strategic talent consulting — R6 delivers senior capability without the overhead of a large firm.
              </p>
            </div>
            <div className="cs-overview-method">
              <div className="cs-method-card">
                <div className="cs-method-label">Our Methodology</div>
                <div className="cs-method-phases">
                  <div className="cs-method-phase">
                    <span className="cs-method-num">01</span>
                    <div>
                      <strong>Assess</strong>
                      <p>Comprehensive diagnostic of current capabilities and needs</p>
                    </div>
                  </div>
                  <div className="cs-method-phase">
                    <span className="cs-method-num">02</span>
                    <div>
                      <strong>Design</strong>
                      <p>Tailored solutions grounded in your context</p>
                    </div>
                  </div>
                  <div className="cs-method-phase">
                    <span className="cs-method-num">03</span>
                    <div>
                      <strong>Implement</strong>
                      <p>Hands-on execution alongside your team</p>
                    </div>
                  </div>
                  <div className="cs-method-phase">
                    <span className="cs-method-num">04</span>
                    <div>
                      <strong>Optimize</strong>
                      <p>Continuous measurement and refinement</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pillars */}
      {pillars.map((pillar, pi) => (
        <section 
          key={pillar.id} 
          className={`cs-pillar ${pi % 2 === 0 ? 'light' : 'dark'}`} 
          data-testid={`cs-pillar-${pillar.id}`}
        >
          <div className="cs-container">
            <div className="cs-pillar-header">
              <div className="cs-pillar-badge">{pillar.label}</div>
              <h2>{pillar.title}</h2>
              <p>{pillar.description}</p>
            </div>
            <div className="cs-offerings-grid">
              {pillar.offerings.map((offering, oi) => (
                <div key={oi} className="cs-offering-card" data-testid={`cs-offering-${offering.num}`}>
                  <div className="cs-offering-num">{offering.num}</div>
                  <h3>{offering.title}</h3>
                  <p className="cs-offering-desc">{offering.desc}</p>
                  <div className="cs-offering-format">{offering.format}</div>
                </div>
              ))}
            </div>
          </div>
        </section>
      ))}

      {/* Engagement Models */}
      <section className="cs-engage" data-testid="cs-engage">
        <div className="cs-container">
          <div className="cs-engage-header">
            <h2>How We Engage</h2>
            <p>Flexible engagement models designed to meet organizations where they are.</p>
          </div>
          <div className="cs-engage-grid">
            {engagementModels.map((model, i) => (
              <div key={i} className="cs-engage-card" data-testid={`cs-engage-${i}`}>
                <div className="cs-engage-num">{String(i + 1).padStart(2, '0')}</div>
                <h3>{model.title}</h3>
                <p>{model.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cs-cta" data-testid="cs-cta">
        <div className="cs-container">
          <h2>Ready to Strengthen Your Organization?</h2>
          <p>Schedule a discovery conversation or take our organizational assessment to identify your highest-impact opportunities.</p>
          <div className="cs-cta-buttons">
            <a href="https://form.jotform.com/260186646305054" className="cs-btn cs-btn-gold" target="_blank" rel="noopener noreferrer" data-testid="cs-cta-contact">
              Schedule a Discovery Call
            </a>
            <button className="cs-btn cs-btn-outline" onClick={() => navigate('/assessment')} data-testid="cs-cta-assessment">
              Take the Right 6 Assessment
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ConsultingSolutionsPage;
